"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { ArrowUp, ChevronDown } from "lucide-react";
import { useRouter } from "next/navigation";
import { FEATURE_TURNSTILE_PUBLIC_SEARCH, TURNSTILE_PUBLIC_SEARCH_SITE_KEY } from "@/config";
import { getInvisibleTurnstileToken } from "@/lib/turnstile/client";
import { useAuth } from "@/contexts/auth-context";
import { AuthModal } from "@/components/auth/AuthModal";
import { useChatContext } from "@/contexts/ChatContext";
import { useTranslation } from "react-i18next";
import { SearchIcon, StoriesIcon, BarChartIcon, ChatIcon } from "@/components/icons";
import { AnimatePresence, motion } from "framer-motion";

const PENDING_QUERY_STORAGE_KEY = "lema_pending_chat_query";
const ACTION_CYCLE_INTERVAL = 8000; // 8 seconds to match hero text animation

export default function MarketingCenteredBrandSearch() {
  const [inputValue, setInputValue] = useState("");
  const [isResponding, setIsResponding] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const router = useRouter();
  const [selectedAction, setSelectedAction] = useState<"search_brands" | "critique_post" | "engagement_rate" | "create_script">("critique_post");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasManualSelection, setHasManualSelection] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const { setInitialMessage, setInputMessage } = useChatContext();
  const { t } = useTranslation();

  // Cycling actions every 8 seconds - synced with hero text (4 positions)
  // Hero text: Grow -> Monetize -> Find Brand Deals -> Understand their Audience
  // Button:    Evaluate -> Evaluate -> Create Script -> Calculate Engagement Rate
  // Note: "Monetize" displays as "Understand Content Performance" in some translations
  const actionsSequence: Array<"critique_post" | "search_brands" | "engagement_rate" | "create_script"> = [
    "create_script",   // 0: "co-creates with you, anytime" -> Create Script
    "engagement_rate", // 1: "decodes your analytics" -> Calculate Engagement Rate
    "critique_post",   // 2: "understand all of your content" -> Evaluate my profile
  ];

  // Unique actions for dropdown (no duplicates) - dynamically generated from actionsSequence
  const uniqueActions = [...new Set(actionsSequence)];

  const [cycleIndex, setCycleIndex] = useState(0);

  // Background cycle that always runs - keeps button in sync with hero text
  useEffect(() => {
    const interval = setInterval(() => {
      setCycleIndex((current) => {
        const nextIndex = (current + 1) % actionsSequence.length;
        return nextIndex;
      });
    }, ACTION_CYCLE_INTERVAL); // 8 seconds to match hero text animation

    return () => clearInterval(interval);
  }, []); // Empty deps - runs once and never stops

  // Update selectedAction based on cycleIndex, but only when not interacting AND no manual selection
  useEffect(() => {
    if (inputValue.trim().length === 0 && !isDropdownOpen && !hasManualSelection) {
      setSelectedAction(actionsSequence[cycleIndex]);
    }
  }, [cycleIndex, inputValue, isDropdownOpen, hasManualSelection]);

  // Clear manual selection when user starts typing
  useEffect(() => {
    if (inputValue.trim().length > 0 && hasManualSelection) {
      setHasManualSelection(false);
    }
  }, [inputValue, hasManualSelection]);

  const getTurnstileToken = async (): Promise<string | null> =>
    await getInvisibleTurnstileToken(TURNSTILE_PUBLIC_SEARCH_SITE_KEY, FEATURE_TURNSTILE_PUBLIC_SEARCH);

  // Centralized action configuration
  const actionConfig = useMemo(() => ({
    engagement_rate: {
      placeholder: t("landing.placeholder_engagement", "Type your Instagram / TikTok / YouTube profile link"),
      icon: <BarChartIcon />,
      text: t("landing.action_text_engagement", "Calculate Engagement Rate")
    },
    critique_post: {
      placeholder: t("landing.placeholder_evaluate", "Type your Instagram / TikTok / YouTube profile link"),
      icon: <StoriesIcon />,
      text: t("landing.action_text_evaluate", "Evaluate my profile")
    },
    create_script: {
      placeholder: t("landing.placeholder_create_script", "Type your Instagram / TikTok / YouTube link"),
      icon: <ChatIcon />,
      text: t("landing.action_text_create_script", "10-days Content Calendar")
    },
    // search_brands: {
    //   placeholder: t("landing.placeholder_find_brand", "Type the brand that you want"),
    //   icon: <SearchIcon />,
    //   text: t("landing.action_text_find_brand", "Find Brand Sponsorship")
    // },
  }), [t]);

  const getPlaceholderText = () => actionConfig[selectedAction].placeholder;
  const getActionIcon = () => actionConfig[selectedAction].icon;
  const getActionText = () => actionConfig[selectedAction].text;

  const handleActionClick = (action: "search_brands" | "critique_post" | "engagement_rate" | "create_script") => {
    setSelectedAction(action);
    setIsDropdownOpen(false);
    setHasManualSelection(true);
    // Background cycle continues independently
    // Manual selection persists until user types, then auto-cycle resumes
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const runBrandSearch = async (text?: string) => {
    const raw = (text ?? inputValue).trim();
    if (!raw || isResponding) return;

    const actionLabel = getActionText();
    let q: string;

    // Special message format for 10-days Content Calendar
    if (selectedAction === "create_script") {
      const prompt = t("landing.prompt_create_content_calendar", "create 10 days content calendar for");
      q = `${prompt} ${raw}`;
    } else {
      q = `${actionLabel}: ${raw}`;
    }

    setInitialMessage(q);
    setInputMessage(q);

    if (!user) {
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(PENDING_QUERY_STORAGE_KEY, q);
          // eslint-disable-next-line no-console
          console.log("[Landing] Stored pending chat query to send after auth:", q);
        } catch { }
      }
      setShowAuthDialog(true);
      return;
    }

    setIsResponding(true);
    setIsTransitioning(true);

    let token: string | null = null;
    try { token = await getTurnstileToken(); } catch { }
    router.push(`/chat?query=${encodeURIComponent(q)}${token ? `&ts_token=${encodeURIComponent(token)}` : ""}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
  };

  const canSendMessage = !isResponding && inputValue.trim().length > 0;

  const onSubmit = async () => {
    await runBrandSearch();
  };


  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;

    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(PENDING_QUERY_STORAGE_KEY);
    } catch { }

    if (!stored) return;

    try {
      window.localStorage.removeItem(PENDING_QUERY_STORAGE_KEY);
    } catch { }

    const q = stored;

    setInitialMessage(q);
    setInputMessage(q);
    // eslint-disable-next-line no-console
    console.log("[Landing] Restored pending chat query after auth, redirecting to /chat:", q);
    setIsResponding(true);
    setIsTransitioning(true);

    const redirectToChat = async () => {
      let token: string | null = null;
      try {
        token = await getTurnstileToken();
      } catch { }
      router.push(`/chat?query=${encodeURIComponent(q)}${token ? `&ts_token=${encodeURIComponent(token)}` : ""}`);
    };

    void redirectToChat();
  }, [user, router, setInitialMessage, setInputMessage]);

  return (
    <>
      <div className={`relative z-10 flex flex-col items-center justify-center transition-all duration-300 ease-in-out ${isTransitioning ? "transform translate-y-24 opacity-0 scale-95" : ""
        }`}>
        {/* Ambient Glow Area */}

        {/* Centered Input Container */}
        <div className="relative w-full max-w-3xl mx-auto px-4">
          <div ref={dropdownRef} className="relative z-20">
            <div className="relative flex flex-col md:flex-row items-stretch backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl focus-within:border-white/40 transition-colors overflow-hidden">
              {/* Action Button */}
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDropdownOpen(!isDropdownOpen);
                  }}
                  className={`group w-full md:w-auto h-full px-4 py-3 backdrop-blur-md rounded-t-xl md:rounded-t-none md:rounded-l-xl transition-all duration-200 text-[17px] md:text-base flex items-center justify-center md:justify-start gap-2 bg-purple-500/20 border-b md:border-b-0 md:border-r border-purple-300/20 text-purple-200 hover:bg-purple-500/30 hover:text-white`}
                  disabled={isResponding}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedAction}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center gap-2"
                    >
                      <span className={selectedAction ? "text-purple-300 group-hover:text-white" : "text-neutral-500 group-hover:text-white"}>
                        {getActionIcon()}
                      </span>
                      <span className="leading-snug">{getActionText()}</span>
                    </motion.div>
                  </AnimatePresence>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
                </button>
              </div>

              {/* Text Input and Send Button Row */}
              <div className="flex-1 flex items-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedAction}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-1 flex items-center"
                  >
                    <TextareaAutosize
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder={getPlaceholderText()}
                      className="flex-1 bg-transparent border-none outline-none resize-none text-base text-white placeholder-neutral-400 px-4 min-h-[48px] leading-6 py-3"
                      disabled={isResponding}
                      rows={1}
                      maxRows={4}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Send Button */}
                <button
                  type="button"
                  className={`flex-shrink-0 p-3 m-1.5 transition-colors rounded-lg ${
                    canSendMessage
                      ? "bg-highlight hover:bg-highlight/90 text-highlight-foreground"
                      : "bg-highlight/40 text-highlight-foreground cursor-not-allowed animate-pulse"
                  }`}
                  onClick={onSubmit}
                  disabled={!canSendMessage}
                >
                  <ArrowUp className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Dropdown Menu - Positioned outside main container */}
            <div>
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 right-0 md:right-auto mt-2 w-full md:w-80 z-[100] backdrop-blur-md bg-black/90 border border-white/20 rounded-xl shadow-2xl overflow-hidden"
                  >
                    {uniqueActions
                      .filter((action) => action !== selectedAction)
                      .map((action) => (
                        <button
                          key={action}
                          type="button"
                          onClick={() => handleActionClick(action)}
                          className="group w-full text-left p-4 transition-all duration-200 text-[15px] bg-white/5 hover:bg-purple-500/20 border-b border-white/10 last:border-b-0"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-neutral-400 group-hover:text-purple-300 transition-colors">
                              {actionConfig[action].icon}
                            </span>
                            <span className="text-neutral-300 group-hover:text-white transition-colors leading-snug">
                              {actionConfig[action].text}
                            </span>
                          </div>
                        </button>
                      ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>


      </div>
      <AuthModal
        open={showAuthDialog}
        onOpenChange={(open) => {
          setShowAuthDialog(open);
          if (!open) {
            setIsResponding(false);
            setIsTransitioning(false);
          }
        }}
        onAuthSuccess={() => setShowAuthDialog(false)}
      />
    </>
  );
}
